import { Col, Input, Row, Select, Spin } from "antd"
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { getDocumentSeries } from "../../../redux/actions/API/documentSeriesAction";
import SeriesMetaDataModal from "../SeriesMetaData/SeriesMetaDataModal";
import { getUsers } from "../../../redux/actions/API/users";
import './GateEntryMetaDataForm.css';
import { setDocumentType } from "../../../redux/actions/UI/createDocument";
import Loader from "../../../Home/Loader/Loader";

const ID_PROOF_OPTIONS: { label: string; value: string }[] = [
    { label: "Aadhar Card", value: "Aadhar" },
    { label: "PAN Card", value: "PAN" },
    { label: "Driving License", value: "Driving License" },
    { label: "Passport", value: "Passport" },
];

const VEHICLE_TYPE_OPTIONS = [
    { label: "Car", value: "Car" },
    { label: "Bike", value: "Bike" },
    { label: "Van", value: "Van" },
    { label: "Truck", value: "Truck" },
    { label: "Auto Rickshaw", value: "Auto Rickshaw" },
    { label: "Other", value: "Other" },
];

const PURPOSE_OPTIONS = [
    { label: "Meeting", value: "Meeting" },
    { label: "Interview", value: "Interview" },
    { label: "Delivery", value: "Delivery" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Pickup", value: "Pickup" },
    { label: "Audit", value: "Audit" },
    { label: "Other", value: "Other" },
];

interface IGateEntryMetaDataFormProps {
  seriesData: any;
  getDocumentSeries: Function;
  companyId: any;
  gateEntryForm: any;
  setGateEntryForm: Function;
  getUsers: Function;
  loadingUsers: boolean;
  loadingSeries: boolean;
  usersData : any;
  setDocumentType: Function;
}

const GateEntryMetaDataForm: React.FC<IGateEntryMetaDataFormProps> = ({
  seriesData,
  getDocumentSeries,
  companyId,
  gateEntryForm,
  setGateEntryForm,
  getUsers,
  loadingUsers,
  loadingSeries,
  usersData,
  setDocumentType,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [purposeDropdownValue, setPurposeDropdownValue] = useState<string>("");
  const [personToMeetDropdownValue, setPersonToMeetDropdownValue] = useState<string>("");
  const [showInitialLoader, setShowInitialLoader] = useState<boolean>(true);
  const [usersLoadedOnce, setUsersLoadedOnce] = useState<boolean>(false);
  const [seriesLoadedOnce, setSeriesLoadedOnce] = useState<boolean>(false);
  const previousLoadingUsersRef = useRef<boolean>(loadingUsers);
  const previousLoadingSeriesRef = useRef<boolean>(loadingSeries);

  const personToMeetOptions = useMemo(() => {
    const userOptions = (usersData || []).map((user: any) => ({
      label: user.name || user.username || user.email,
      value: user.name || user.username || user.email,
    }));
    return [...userOptions, { value: "Other", label: "Other" }];
  }, [usersData]);

  const handleSeriesModalClose = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    setDocumentType("Gate Entry");
    getDocumentSeries({ companyId: Number(companyId) });
    getUsers({companyId: Number(companyId)})
  }, []);

  useEffect(() => {
    if (seriesData) {
      generateSeriesData();
    }
  }, [seriesData]);

  useEffect(() => {
    if (previousLoadingUsersRef.current && !loadingUsers) {
      setUsersLoadedOnce(true);
    }

    if (previousLoadingSeriesRef.current && !loadingSeries) {
      setSeriesLoadedOnce(true);
    }

    previousLoadingUsersRef.current = loadingUsers;
    previousLoadingSeriesRef.current = loadingSeries;
  }, [loadingUsers, loadingSeries]);

  useEffect(() => {
    if (usersLoadedOnce && seriesLoadedOnce) {
      setShowInitialLoader(false);
    }
  }, [usersLoadedOnce, seriesLoadedOnce]);

  const documentType = "Gate Entry";

  const handleSeriesSelection = (series: any) => {
    setGateEntryForm((prev: any) => ({
      ...prev,
      documentNumber: `${series.prefix}${series.nextNumber}`,
      seriesId: series.id,
    }));
  };

  const generateSeriesData = () => {
    const filteredSeries = seriesData?.filter(
      (series: { default: number; DocType: string }) =>
        series.default == 1 && series.DocType == documentType
    );

    if (filteredSeries && filteredSeries.length > 0) {
      const { prefix, nextNumber } = filteredSeries[0];
      setGateEntryForm((prev: any) => ({
        ...prev,
        documentNumber: `${prefix}${nextNumber}`,
        seriesId: filteredSeries[0].id,
      }));
    }
  };

  const updateForm = (field: string, value: any) => {
    setGateEntryForm((prev: any) => ({ ...prev, [field]: value }));
  };

  // --- Row 1: Document Number, Visitor Name, Visitor Contact ---
  const renderDocumentNumber = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <div className="flexBox rowMobileDocumentLabel">
        <label>
          Visitor Pass ID 
          <span className="deleteIcon">*</span>
        </label>
        <span className="expoText" onClick={() => setIsVisible(true)}>
          Customize
        </span>
      </div>
      <Input
        placeholder="Please enter Gate Entry document number"
        value={gateEntryForm.documentNumber}
        disabled
        style={{ marginTop: "5px" }}
      />
    </Col>
  );

  const renderVisitorName = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        Visitor Name<span className="deleteIcon">*</span>
      </label>
      <Input
        placeholder="Enter visitor name"
        value={gateEntryForm.visitorName}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("visitorName", ev.target.value)}
      />
    </Col>
  );

  const renderVisitorContact = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        Mobile Number<span className="deleteIcon">*</span>
      </label>
      <Input
        placeholder="Enter visitor contact"
        value={gateEntryForm.visitorContact}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("visitorContact", ev.target.value)}
      />
    </Col>
  );

  // --- Row 2: Visitor Email, Visitor Company, ID Proof Type ---
  const renderVisitorEmail = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Email</label>
      <Input
        placeholder="Enter visitor email"
        value={gateEntryForm.visitorEmail}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("visitorEmail", ev.target.value)}
      />
    </Col>
  );

  const renderVisitorCompany = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Company Name</label>
      <Input
        placeholder="Enter visitor company"
        value={gateEntryForm.visitorCompany}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("visitorCompany", ev.target.value)}
      />
    </Col>
  );

  const renderIdProofType = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>ID Proof Type</label>
      <Select
        placeholder="Select ID Proof Type"
        value={gateEntryForm.idProofType || undefined}
        style={{ width: "100%", marginTop: "5px" }}
        allowClear
        onChange={(value) => {
          updateForm("idProofType", value || "");
          if (!value) updateForm("idProofNumber", "");
        }}
        options={ID_PROOF_OPTIONS}
      />
    </Col>
  );

  // --- Row 3: ID Proof Number (conditional), Purpose of Visit, custom purpose / Person to Meet ---
  const renderIdProofNumber = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        ID Proof Number<span className="deleteIcon">*</span>
      </label>
      <Input
        placeholder={`Enter ${gateEntryForm.idProofType} number`}
        value={gateEntryForm.idProofNumber}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("idProofNumber", ev.target.value)}
      />
    </Col>
  );

  const renderPurposeOfVisit = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        Purpose of Visit<span className="deleteIcon">*</span>
      </label>
      <Select
        placeholder="Select Purpose of Visit"
        value={purposeDropdownValue || undefined}
        style={{ width: "100%", marginTop: "5px" }}
        onChange={(value) => {
          setPurposeDropdownValue(value);
          if (value !== "Other") {
            updateForm("purposeOfVisit", value);
          } else {
            updateForm("purposeOfVisit", "");
          }
        }}
        options={PURPOSE_OPTIONS}
      />
    </Col>
  );

  const renderCustomPurpose = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        Reason for Visit<span className="deleteIcon">*</span>
      </label>
      <Input
        placeholder="Enter purpose of visit"
        value={gateEntryForm.purposeOfVisit}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("purposeOfVisit", ev.target.value)}
      />
    </Col>
  );

  const renderPersonToMeet = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Person to Meet<span className="deleteIcon">*</span></label>
      <Select
        placeholder="Select Person to Meet"
        value={personToMeetDropdownValue || undefined}
        style={{ width: "100%", marginTop: "5px" }}
        loading={loadingUsers}
        notFoundContent={loadingUsers ? <Spin size="small" /> : "No users found"}
        showSearch
        filterOption={(input, option) =>
          (option?.label as string)?.toLowerCase()?.includes(input?.toLowerCase())
        }
        onChange={(value) => {
          setPersonToMeetDropdownValue(value);
          if (value !== "Other") {
            updateForm("personToMeet", value);
          } else {
            updateForm("personToMeet", "");
          }
        }}
        options={personToMeetOptions}
      />
    </Col>
  );

  const renderCustomPersonToMeet = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>
        Person Name<span className="deleteIcon">*</span>
      </label>
      <Input
        placeholder="Enter person to meet"
        value={gateEntryForm.personToMeet}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("personToMeet", ev.target.value)}
      />
    </Col>
  );

  // --- Row 5: Vehicle Type, Vehicle Number, Comments ---
  const renderVehicleType = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Vehicle Type</label>
      <Select
        placeholder="Select Vehicle Type"
        value={gateEntryForm.vehicleType || undefined}
        style={{ width: "100%", marginTop: "5px" }}
        allowClear
        onChange={(value) => updateForm("vehicleType", value || "")}
        options={VEHICLE_TYPE_OPTIONS}
      />
    </Col>
  );

  const renderVehicleNumber = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Vehicle Number</label>
      <Input
        placeholder="Enter vehicle number"
        value={gateEntryForm.vehicleNumber}
        style={{ marginTop: "5px" }}
        onChange={(ev) => updateForm("vehicleNumber", ev.target.value)}
      />
    </Col>
  );

  const renderComments = () => (
    <Col className="gutter-row" span={8} xs={24} sm={24} lg={8}>
      <label>Comments</label>
      <Input.TextArea
        placeholder="Enter comments"
        value={gateEntryForm.comments}
        style={{ marginTop: "5px", maxHeight : "100px" }}
        rows={1}
        onChange={(ev) => updateForm("comments", ev.target.value)}
      />
    </Col>
  );

  return (
    <>
      <Loader loading={showInitialLoader} />
      <div className="documentMetaDataForm">
        <div className="flexBox mobAddCategory">
          <div style={{ width: "100%" }}>
            <div className="containerDocumentMobileView">
              {/* Row 1: Document Number, Visitor Name, Visitor Contact */}
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-10">
                {renderDocumentNumber()}
                {renderVisitorName()}
                {renderVisitorContact()}
              </Row>

              {/* Row 2: Visitor Email, Visitor Company, ID Proof Type */}
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-10">
                {renderVisitorEmail()}
                {renderVisitorCompany()}
                {renderIdProofType()}
              </Row>

              {/* Row 3: ID Proof Number (if type selected), Purpose of Visit, conditional fields */}
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-10">
                {gateEntryForm.idProofType && renderIdProofNumber()}
                {renderPurposeOfVisit()}
                {purposeDropdownValue === "Other" && renderCustomPurpose()}
              </Row>

              {/* Row 4: Person to Meet, custom person (if Other), Vehicle Type */}
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-10">
                {renderPersonToMeet()}
                {personToMeetDropdownValue === "Other" && renderCustomPersonToMeet()}
                {renderVehicleType()}
              </Row>

              {/* Row 5: Vehicle Number, Comments */}
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-10">
                {renderVehicleNumber()}
                {renderComments()}
              </Row>
            </div>
          </div>
        </div>
      </div>
      <SeriesMetaDataModal
        isVisible={isVisible}
        onClose={handleSeriesModalClose}
        onSelectSeries={handleSeriesSelection}
        setIsVisible={setIsVisible}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
      seriesData: state.api.getDocumentSeries?.data,
    companyId: state.api.login?.data.companyId,
    usersData : state.api.getUsers?.data,
  loadingUsers : state.api.getUsers?.loading,
  loadingSeries: state.api.getDocumentSeries?.loading,

});
const mapDispatchToProps = (dispatch: any) => ({
      getDocumentSeries: (payload: any) => dispatch(getDocumentSeries(payload)),
      getUsers: (payload: any)=> dispatch(getUsers(payload)),
      setDocumentType: (payload: any) => dispatch(setDocumentType(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GateEntryMetaDataForm);